import React, { useEffect, useRef, useState } from "react";
import bodymovin, { AnimationItem } from "lottie-web";

import heroAnimation from "./assets/hero-main.json";
import introAnimationData from "./assets/hero-auto-play.json";
import centerAnimationData from "./assets/hero-circle.json";
import tooltipImage from "./assets/tooltip.svg";
import {
  AnimationContainer,
  Container,
  HeroContent,
  HeroSubtitle,
  HeroTitle,
} from "./styles";
import { AnimatedTitle } from "../../components/commons/AnimatedTitle";

export const HomepageHero = () => {
  const containerRef = useRef(null);
  const introRef = useRef(null);
  const centerLoopRef = useRef(null);
  const [animation, setAnimation] = useState<AnimationItem | null>(null);
  const [introAnimation, setIntroAnimation] = useState<AnimationItem | null>(
    null
  );
  const [centerAnimation, setCenterAnimation] = useState<AnimationItem | null>(
    null
  );
  const [currentScene, setCurrentScene] = useState<string>("initial");
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const currentSceneRef = useRef<string>();
  currentSceneRef.current = currentScene;

  const applyFrameListener = (currentTime: any, elementId: string) => {
    const centerFinishTime = 28;
    const backFinishTime = 31;

    //Landed on Center scene
    if (
      currentTime == centerFinishTime &&
      currentSceneRef.current === `word-${elementId}`
    ) {
      setCurrentScene(`center-${elementId}`);
      introAnimation.stop();
      centerLoopRef.current.style.display = "block";
      centerAnimation.play();
      let centerButton = document.querySelector<HTMLElement>("#s01_obj02_in");
      centerButton.style.cursor = "pointer";
      centerButton.addEventListener("click", () =>
        handleElementClick("centerButton")
      );
    }

    //Landed on Initial Scene
    if (
      currentTime == backFinishTime &&
      currentSceneRef.current === `back-${elementId}`
    ) {
      setCurrentScene("initial");
      animation.setSegment(0, animation.totalFrames);
      animation.goToAndStop(0);
      animation.goToAndStop(0, true);
      introAnimation.play();
      let centerButton = document.querySelector<HTMLElement>("#s01_obj02_in");
      centerButton.style.cursor = "default";
    }
  };

  const applyClickAnimation = (
    elementId: string,
    centerClickStartFrame: number,
    centerClickEndFrame: number,
    initialClickStartFrame: number,
    initialClickEndFrame: number
  ) => {
    if (currentSceneRef.current === `center-${elementId}`) {
      //center click
      setCurrentScene(`back-${elementId}`);
      centerLoopRef.current.style.display = "none";
      animation.playSegments(
        [centerClickStartFrame, centerClickEndFrame],
        true
      );
    }
    if (
      currentSceneRef.current === "initial" ||
      currentSceneRef.current === `hover-${elementId}`
    ) {
      //initial click
      introAnimation.stop();
      animation.playSegments(
        [initialClickStartFrame, initialClickEndFrame],
        true
      );
      setCurrentScene(`word-${elementId}`);
    }
  };

  const applyHoverAnimation = (
    elementId: string,
    centerHoverStartFrame: number,
    centerHoverEndFrame: number,
    initialHoverStartFrame: number,
    initialHoverEndFrame: number
  ) => {
    //center hover
    if (currentSceneRef.current === `center-${elementId}`) {
      animation.playSegments(
        [centerHoverStartFrame, centerHoverEndFrame],
        true
      );
    }
    //initial hover
    else if (animation.currentFrame === 0) {
      introAnimation.stop();
      animation.playSegments(
        [initialHoverStartFrame, initialHoverEndFrame],
        true
      );
      setCurrentScene(`hover-${elementId}`);
    }
  };

  const applyLeaveAnimation = (
    elementId: string,
    centerHoverStartFrame: number,
    centerHoverEndFrame: number,
    initialHoverStartFrame: number,
    initialHoverEndFrame: number
  ) => {
    if (currentSceneRef.current === `hover-${elementId}`) {
      //initial hover leave
      animation.setDirection(-1);
      animation.playSegments(
        [initialHoverEndFrame, initialHoverStartFrame],
        true
      );
      introAnimation.play();
      setCurrentScene("initial");
    } else if (currentSceneRef.current === `center-${elementId}`) {
      //center hover leave
      animation.setDirection(-1);
      animation.playSegments(
        [centerHoverEndFrame, centerHoverStartFrame],
        true
      );
      animation.goToAndStop(48);
    }
  };

  type InteractiveElement = {
    element: string;
    centerClickStartFrame: number;
    centerClickEndFrame: number;
    initialClickStartFrame: number;
    initialClickEndFrame: number;
    centerHoverStartFrame: number;
    centerHoverEndFrame: number;
    initialHoverStartFrame: number;
    initialHoverEndFrame: number;
  };

  type InteractiveElementsType = Record<string, InteractiveElement>;

  const InteractiveElements: InteractiveElementsType = {
    Seamless: {
      element: "#s01_text01_in",
      //clicks
      centerClickStartFrame: 55,
      centerClickEndFrame: 87,
      initialClickStartFrame: 19,
      initialClickEndFrame: 48,
      //hovers:
      centerHoverStartFrame: 51,
      centerHoverEndFrame: 54,
      initialHoverStartFrame: 8,
      initialHoverEndFrame: 16,
    },
    Growth: {
      element: "#s01_text02_in",
      //clicks
      centerClickStartFrame: 136,
      centerClickEndFrame: 168,
      initialClickStartFrame: 100,
      initialClickEndFrame: 129,
      //hovers:
      centerHoverStartFrame: 132,
      centerHoverEndFrame: 135,
      initialHoverStartFrame: 89,
      initialHoverEndFrame: 96,
    },
    Tireless: {
      element: "#s01_text03_in",
      //clicks
      centerClickStartFrame: 217,
      centerClickEndFrame: 249,
      initialClickStartFrame: 181,
      initialClickEndFrame: 210,
      //hovers:
      centerHoverStartFrame: 213,
      centerHoverEndFrame: 216,
      initialHoverStartFrame: 170,
      initialHoverEndFrame: 177,
    },
    Collaboration: {
      element: "#s01_text04_in ",
      //clicks
      centerClickStartFrame: 298,
      centerClickEndFrame: 330,
      initialClickStartFrame: 262,
      initialClickEndFrame: 291,
      //hovers:
      centerHoverStartFrame: 294,
      centerHoverEndFrame: 297,
      initialHoverStartFrame: 251,
      initialHoverEndFrame: 258,
    },
    Dynamic: {
      element: "#s01_text05_in",
      //clicks
      centerClickStartFrame: 379,
      centerClickEndFrame: 411,
      initialClickStartFrame: 343,
      initialClickEndFrame: 372,
      //hovers:
      centerHoverStartFrame: 375,
      centerHoverEndFrame: 378,
      initialHoverStartFrame: 332,
      initialHoverEndFrame: 339,
    },
    Commitment: {
      element: "#s01_text06_in",
      //clicks
      centerClickStartFrame: 460,
      centerClickEndFrame: 492,
      initialClickStartFrame: 424,
      initialClickEndFrame: 453,
      //hovers:
      centerHoverStartFrame: 456,
      centerHoverEndFrame: 459,
      initialHoverStartFrame: 413,
      initialHoverEndFrame: 420,
    },
    Empowered: {
      element: "#s01_text07_in",
      //clicks
      centerClickStartFrame: 541,
      centerClickEndFrame: 573,
      initialClickStartFrame: 505,
      initialClickEndFrame: 534,
      //hovers:
      centerHoverStartFrame: 537,
      centerHoverEndFrame: 540,
      initialHoverStartFrame: 494,
      initialHoverEndFrame: 501,
    },
    Teams: {
      element: "#s01_text08_in",
      //clicks
      centerClickStartFrame: 622,
      centerClickEndFrame: 654,
      initialClickStartFrame: 586,
      initialClickEndFrame: 615,
      //hovers:
      centerHoverStartFrame: 618,
      centerHoverEndFrame: 621,
      initialHoverStartFrame: 575,
      initialHoverEndFrame: 582,
    },
    Holistic: {
      element: "#s01_text09_in",
      //clicks
      centerClickStartFrame: 703,
      centerClickEndFrame: 735,
      initialClickStartFrame: 667,
      initialClickEndFrame: 696,
      //hovers:
      centerHoverStartFrame: 699,
      centerHoverEndFrame: 702,
      initialHoverStartFrame: 656,
      initialHoverEndFrame: 663,
    },
    Strategy: {
      element: "#s01_text10_in",
      //clicks
      centerClickStartFrame: 784,
      centerClickEndFrame: 816,
      initialClickStartFrame: 748,
      initialClickEndFrame: 777,
      //hovers:
      centerHoverStartFrame: 780,
      centerHoverEndFrame: 783,
      initialHoverStartFrame: 737,
      initialHoverEndFrame: 744,
    },
    Tangible: {
      element: "#s01_text11_in",
      //clicks
      centerClickStartFrame: 865,
      centerClickEndFrame: 897,
      initialClickStartFrame: 829,
      initialClickEndFrame: 858,
      //hovers:
      centerHoverStartFrame: 861,
      centerHoverEndFrame: 864,
      initialHoverStartFrame: 818,
      initialHoverEndFrame: 825,
    },
    Results: {
      element: "#s01_text12_in",
      //clicks
      centerClickStartFrame: 946,
      centerClickEndFrame: 978,
      initialClickStartFrame: 910,
      initialClickEndFrame: 939,
      //hovers:
      centerHoverStartFrame: 942,
      centerHoverEndFrame: 945,
      initialHoverStartFrame: 899,
      initialHoverEndFrame: 906,
    },
    Personalized: {
      element: "#s01_text13_in",
      //clicks
      centerClickStartFrame: 1027,
      centerClickEndFrame: 1059,
      initialClickStartFrame: 991,
      initialClickEndFrame: 1020,
      //hovers:
      centerHoverStartFrame: 1023,
      centerHoverEndFrame: 1026,
      initialHoverStartFrame: 980,
      initialHoverEndFrame: 987,
    },
    Solutions: {
      element: "#s01_text14_in",
      //clicks
      centerClickStartFrame: 1108,
      centerClickEndFrame: 1140,
      initialClickStartFrame: 1072,
      initialClickEndFrame: 1101,
      //hovers:
      centerHoverStartFrame: 1104,
      centerHoverEndFrame: 1107,
      initialHoverStartFrame: 1061,
      initialHoverEndFrame: 1068,
    },
    Engaging: {
      element: "#s01_text15_in",
      //clicks
      centerClickStartFrame: 1189,
      centerClickEndFrame: 1221,
      initialClickStartFrame: 1153,
      initialClickEndFrame: 1182,
      //hovers:
      centerHoverStartFrame: 1185,
      centerHoverEndFrame: 1188,
      initialHoverStartFrame: 1142,
      initialHoverEndFrame: 1149,
    },
    Experiences: {
      element: "#s01_text16_in",
      //clicks
      centerClickStartFrame: 1270,
      centerClickEndFrame: 1302,
      initialClickStartFrame: 1234,
      initialClickEndFrame: 1263,
      //hovers:
      centerHoverStartFrame: 1266,
      centerHoverEndFrame: 1269,
      initialHoverStartFrame: 1223,
      initialHoverEndFrame: 1230,
    },
    Innovative: {
      element: "#s01_text17_in",
      //clicks
      centerClickStartFrame: 1351,
      centerClickEndFrame: 1383,
      initialClickStartFrame: 1315,
      initialClickEndFrame: 1344,
      //hovers:
      centerHoverStartFrame: 1347,
      centerHoverEndFrame: 1350,
      initialHoverStartFrame: 1304,
      initialHoverEndFrame: 1311,
    },
    Approaches: {
      element: "#s01_text18_in",
      //clicks
      centerClickStartFrame: 1432,
      centerClickEndFrame: 1464,
      initialClickStartFrame: 1396,
      initialClickEndFrame: 1425,
      //hovers:
      centerHoverStartFrame: 1428,
      centerHoverEndFrame: 1431,
      initialHoverStartFrame: 1385,
      initialHoverEndFrame: 1392,
    },
  };

  // handle events
  const handleEnterFrame = (e: any) => {
    if (e?.currentTime) {
      const currentElement = currentSceneRef.current.split("-")[1];
      applyFrameListener(e.currentTime, currentElement);
    }
  };

  const handleElementClick = (elementId: string, reverse?: true) => {
    if (animation) {
      if (currentSceneRef.current.includes("center")) {
        const elementId = currentSceneRef.current.replace("center-", "");
        const element = InteractiveElements[elementId];
        applyClickAnimation(
          elementId,
          element.centerClickStartFrame,
          element.centerClickEndFrame,
          element.initialClickStartFrame,
          element.initialClickEndFrame
        );
      }

      if (elementId in InteractiveElements) {
        const element = InteractiveElements[elementId];
        applyClickAnimation(
          elementId,
          element.centerClickStartFrame,
          element.centerClickEndFrame,
          element.initialClickStartFrame,
          element.initialClickEndFrame
        );
      }
    }
  };

  const handleElementHover = (elementId: string, reverse?: true) => {
    if (animation) {
      if (currentSceneRef.current.includes("center")) {
        const newElementId = currentSceneRef.current.replace("center-", "");
        const element = InteractiveElements[newElementId];

        console.log("hover", element);
        applyHoverAnimation(
          newElementId,
          element.centerHoverStartFrame,
          element.centerHoverEndFrame,
          element.initialHoverStartFrame,
          element.initialHoverEndFrame
        );
      }

      if (elementId in InteractiveElements) {
        const element = InteractiveElements[elementId];
        applyHoverAnimation(
          elementId,
          element.centerHoverStartFrame,
          element.centerHoverEndFrame,
          element.initialHoverStartFrame,
          element.initialHoverEndFrame
        );
      }

      if (
        elementId === "centerButton" &&
        currentSceneRef.current === "initial"
      ) {
        setTooltipVisible(true);
      }
    }
  };

  const handleElementLeave = (elementId: string) => {
    if (animation) {
      if (elementId in InteractiveElements) {
        const element = InteractiveElements[elementId];
        applyLeaveAnimation(
          elementId,
          element.centerHoverStartFrame,
          element.centerHoverEndFrame,
          element.initialHoverStartFrame,
          element.initialHoverEndFrame
        );
      }

      if (elementId === "centerButton") {
        if (currentSceneRef.current.includes("center")) {
          const newElementId = currentSceneRef.current.replace("center-", "");
          const element = InteractiveElements[newElementId];
          console.log("leave", element);

          applyLeaveAnimation(
            newElementId,
            element.centerHoverStartFrame,
            element.centerHoverEndFrame,
            element.initialHoverStartFrame,
            element.initialHoverEndFrame
          );
        }

        setTooltipVisible(false);
      }
    }
  };

  // add listeners
  const addEventListeners = () => {
    addListener("#s01_obj02_in", "centerButton", true);
    for (const key of Object.keys(InteractiveElements)) {
      const element = InteractiveElements[key];
      addListener(element.element, key);
    }
  };

  const addListener = (
    elementQuerySelector: string,
    elementId: string,
    avoidPointer?: boolean
  ) => {
    let element = document.querySelector<HTMLElement>(elementQuerySelector);
    if (!avoidPointer) {
      element.style.cursor = "pointer";
    }
    element.addEventListener("mouseenter", () => handleElementHover(elementId));
    element.addEventListener("mouseleave", () => handleElementLeave(elementId));
    element.addEventListener("click", () => handleElementClick(elementId));
  };

  // initialize animations
  useEffect(() => {
    const introAnim = bodymovin.loadAnimation({
      container: introRef.current,
      renderer: "svg",
      autoplay: true,
      loop: true,

      animationData: introAnimationData,
    });

    const centerLoopAnim = bodymovin.loadAnimation({
      container: centerLoopRef.current,
      renderer: "svg",
      autoplay: false,
      loop: true,

      animationData: centerAnimationData,
    });

    const anim = bodymovin.loadAnimation({
      container: containerRef.current,
      renderer: "svg",
      autoplay: false,
      loop: false,

      animationData: heroAnimation,
    });

    setIntroAnimation(introAnim);
    setCenterAnimation(centerLoopAnim);
    setAnimation(anim);
  }, []);

  useEffect(() => {
    if (currentScene === "initial") {
      let centerButton = document.querySelector<HTMLElement>("#s01_obj01_in");

      centerButton.style.cursor = "none";
    }
  }, [currentScene]);

  useEffect(() => {
    if (animation) {
      animation.addEventListener("DOMLoaded", addEventListeners);
      animation.addEventListener("enterFrame", handleEnterFrame);
    }

    return () => {
      window.removeEventListener("enterFrame", handleEnterFrame);
    };
  }, [animation]);

  return (
    <Container id="herocontainer">
      <HeroContent>
        <AnimatedTitle
          height="220px"
          heightTablet="220px"
          heightMobile="120px  "
          lineDelay={0.5}
        >
          <HeroTitle id="hero">
            Providing <br />
            Digital
            <br />
            Transformation
          </HeroTitle>
        </AnimatedTitle>
        <HeroSubtitle>
          The partner your business needs for digital quality solutions
        </HeroSubtitle>
      </HeroContent>
      <AnimationContainer ref={introRef}></AnimationContainer>
      <img
        src={tooltipImage}
        style={{
          transition: "all 0.3s",
          opacity: tooltipVisible ? "1" : "0",
          visibility: tooltipVisible ? "visible" : "hidden",
          position: "absolute",
          zIndex: "191",
          width: "10%",
          top: "42%",
          right: "12%",
        }}
      />
      <AnimationContainer
        style={{
          display: "none",
          zIndex: "189",
        }}
        ref={centerLoopRef}
      />
      <AnimationContainer
        style={{
          zIndex: "190",
        }}
        ref={containerRef}
      />
    </Container>
  );
};
